import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../store";

// Interfaces for deal and person details
export interface PhoneNumber {
  label: string;
  value: string;
  primary: boolean;
}

export interface PersonDetails {
  id: string;
  name: string;
  phone: PhoneNumber[];
  email?: string[];
  // Add other person fields as needed
}

export interface DealDetails {
  id: string;
  title: string;
  value?: number;
  currency?: string;
  person_id: PersonDetails;
  // Add other deal fields as needed
}

interface DealPersonDetailsState {
  dealDetails: DealDetails | null;
  personDetails: PersonDetails | null;
  phoneNumbers: PhoneNumber[];
  loading: boolean;
  error: string | null;
}

const initialState: DealPersonDetailsState = {
  dealDetails: null,
  personDetails: null,
  phoneNumbers: [],
  loading: false,
  error: null,
};

// Async thunk for fetching deal details
export const fetchDealDetails = createAsyncThunk(
  "dealPersonDetails/fetchDealDetails",
  async (
    { userId, companyId, dealId }: { userId: string; companyId: string; dealId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await fetch(
        `https://api.insightssystem.com/api:MLBAaPmt/deal/details?userId=${userId}&companyId=${companyId}&dealId=${dealId}`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch deal details: ${response.status}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : "Failed to fetch deal details");
    }
  }
);

// Async thunk for fetching person details
export const fetchPersonDetails = createAsyncThunk(
  "dealPersonDetails/fetchPersonDetails",
  async (
    { userId, companyId, personId }: { userId: string; companyId: string; personId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await fetch(
        `https://api.insightssystem.com/api:MLBAaPmt/person/details?userId=${userId}&companyId=${companyId}&personId=${personId}`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch person details: ${response.status}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : "Failed to fetch person details");
    }
  }
);

const dealPersonDetailsSlice = createSlice({
  name: "dealPersonDetails",
  initialState,
  reducers: {
    clearDetails: (state) => {
      state.dealDetails = null;
      state.personDetails = null;
      state.phoneNumbers = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Deal details
      .addCase(fetchDealDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDealDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.dealDetails = action.payload;
        state.personDetails = action.payload.person_id;
        // Extract phone numbers from person_id
        if (action.payload.person_id?.phone && Array.isArray(action.payload.person_id.phone)) {
          state.phoneNumbers = action.payload.person_id.phone;
        } else {
          state.phoneNumbers = [];
        }
      })
      .addCase(fetchDealDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Person details
      .addCase(fetchPersonDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPersonDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.personDetails = action.payload;
        // Extract phone numbers directly from person
        if (action.payload?.phone && Array.isArray(action.payload.phone)) {
          state.phoneNumbers = action.payload.phone;
        } else {
          state.phoneNumbers = [];
        }
      })
      .addCase(fetchPersonDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearDetails } = dealPersonDetailsSlice.actions;

// Selectors
export const selectDealDetails = (state: RootState) => state.dealPersonDetails.dealDetails;
export const selectPersonDetails = (state: RootState) => state.dealPersonDetails.personDetails;
export const selectPhoneNumbers = (state: RootState) => state.dealPersonDetails.phoneNumbers;
export const selectDetailsLoading = (state: RootState) => state.dealPersonDetails.loading;
export const selectDetailsError = (state: RootState) => state.dealPersonDetails.error;

export default dealPersonDetailsSlice.reducer;
